<template lang="html">
  <div :style="`height:${windowHeight}px;width:100%`">
    <v-tooltip
      v-if="params('draft') === 'true'"
    >
      <template v-slot:activator="{ on }">
        <v-btn
          icon
          style="position:absolute;top:24px;left:24px;z-index:1"
          v-on="on"
          @click="bboxPasted()"
        >
          <v-icon>mdi-crop-free</v-icon>
        </v-btn>
      </template>
      <span>Copier la position actuelle</span>
    </v-tooltip>
    <v-progress-linear
      v-if="loading"
      style="position:absolute;z-index:1;max-width:100%;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      style="height:100%"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import colors from '../assets/colors'
import { bbox2zoom } from '@data-fair/components-app/map/utils'
import 'maplibre-gl/dist/maplibre-gl.css'
import { escape } from '@data-fair/components-app/filters/filters'
import eventBus from '../assets/event-bus.js'

const maplibregl = require('maplibre-gl')
const svgToDataURL = require('svg-to-dataurl')

const zoomLevel = {
  housenumber: 17,
  street: 16,
  municipality: 12
}

export default {
  data: () => ({
    loading: true
  }),
  computed: {
    ...mapState(['selected', 'datasets', 'layers']),
    ...mapGetters(['tileserverUrl', 'config', 'datasetInfos'])
  },
  watch: {
    layers: {
      handler (layers) {
        for (const layer of Object.values(layers)) {
          for (const id of layer.ids) {
            this.map.setLayoutProperty(id, 'visibility', layer.visible ? 'visible' : 'none')
            const l = this.config.layers.find(e => e.dataset.id === id)
            const source = this.map.getSource(id)
            if (source && source.tiles) {
              const select = ['_id']
              if (l.colors.type === 'field' && l.colors.field) select.push(l.colors.field)
              else if (l.colors.type === 'enum' && l.colors.enumOptions.field) select.push(l.colors.enumOptions.field)
              else if (l.colors.type === 'range' && l.colors.rangeOptions.field) select.push(l.colors.rangeOptions.field)
              if (l.shapes.type === 'icon-multiple' && l.shapes.field) select.push(l.shapes.field)
              else if (l.shapes.type === 'circle' && l.shapes.radius.field) select.push(l.shapes.radius.field)
              const qs = []
              Object.entries(layer.filters || {}).forEach(([field, value]) => {
                qs.push(`(${escape(field)}:${escape(value)})`)
              })
              const dataTilesUrl = `${this.datasetInfos[id].href}/lines?format=pbf&size=10000&sort=_rand&select=${select.join(',')}${qs.length ? ('&qs=' + qs.join(' AND ')) : ''}&finalizedAt=${this.datasets[id].finalizedAt}&xyz={x},{y},{z}`
              if (source.tiles[0] !== dataTilesUrl) {
                source.tiles = [dataTilesUrl]
                this.map.style.sourceCaches[id].clearTiles()
                this.map.style.sourceCaches[id].update(this.map.transform)
                this.map.triggerRepaint()
              }
            }
          }
        }
      },
      deep: true
    },
    datasets (val) {
      if (!this.map) this.initMap()
    },
    async windowHeight () {
      if (this.map) {
        await this.$nextTick()
        this.map.resize()
      }
    }
  },
  mounted () {
    this.initMap()
    eventBus.$on('goTo', pos => {
      this.map.setZoom(zoomLevel[pos[2]] || 11)
      this.map.setCenter([pos[1], pos[0]])
    })
  },
  methods: {
    initMap () {
      if (!maplibregl || Object.keys(this.datasets).length !== this.config.layers.length) return
      const initialBounds = this.config.bbox && this.config.bbox.length && maplibregl.LngLatBounds.convert(JSON.parse(this.config.bbox))
      // const minZoom = Math.min(...(this.layers || []).map(l => l.minzoom), 6)
      this.map = new maplibregl.Map({
        container: 'map',
        center: initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167],
        zoom: initialBounds ? bbox2zoom(JSON.parse(this.config.bbox)) : 6,
        style: this.tileserverUrl + '/styles/' + this.config.style + '/style.json',
        transformRequest: (url, resourceType) => {
          return {
            url,
            credentials: 'include' // include cookies, for data-fair sessions
          }
        }
      })
      this.map.getCanvas().style.cursor = 'crosshair'
      this.map.addControl(new maplibregl.NavigationControl(), 'top-right')

      // Disable map rotation using right click + drag
      this.map.dragRotate.disable()
      // Disable map rotation using touch rotation gesture
      this.map.touchZoomRotate.disableRotation()

      this.map.on('click', (e) => this.selectionDetails([e.lngLat.lat, e.lngLat.lng]))
      this.map.on('moveend', (e) => { this.loading = true })
      this.map.once('load', () => {
        const labelLayer = this.map.getStyle().layers.find(l => l['source-layer'] === 'place').id
        const layers = {}
        this.config.layers.slice().reverse().forEach(layer => {
          const select = ['_id']
          if (layer.colors.type === 'field' && layer.colors.field) select.push(layer.colors.field)
          else if (layer.colors.type === 'enum' && layer.colors.enumOptions.field) select.push(layer.colors.enumOptions.field)
          else if (layer.colors.type === 'range' && layer.colors.rangeOptions.field) select.push(layer.colors.rangeOptions.field)
          if (layer.shapes.type === 'icon-multiple' && layer.shapes.field) select.push(layer.shapes.field)
          else if (layer.shapes.type === 'circle' && layer.shapes.radius.field) select.push(layer.shapes.radius.field)

          const dataTilesUrl = `${this.datasetInfos[layer.dataset.id].href}/lines?format=pbf&size=10000&sort=_rand&select=${select.join(',')}&finalizedAt=${this.datasets[layer.dataset.id].finalizedAt}&xyz={x},{y},{z}`
          this.map.addSource(layer.dataset.id, { type: 'vector', tiles: [dataTilesUrl], promoteId: '_id' })

          const mapLayer = {
            id: layer.dataset.id,
            source: layer.dataset.id,
            'source-layer': 'results',
            minzoom: 2
          }
          const layerColor = (() => {
            if (layer.colors.type === 'single') {
              return layer.colors.value
            } else if (layer.colors.type === 'field') {
              return ['get', layer.colors.field]
            } else if (layer.colors.type === 'enum') {
              return [].concat('match', [['to-string', ['get', layer.colors.enumOptions.field]]], ...colors(layer.colors).map(c => [c.value, c.color]), layer.colors.default)
            } else if (layer.colors.type === 'range') {
              return ['case', ['has', layer.colors.rangeOptions.field], [].concat('case', ...colors(layer.colors, this.datasets[layer.dataset.id].minValueColor, this.datasets[layer.dataset.id].maxValueColor, this.datasets[layer.dataset.id].quantiles).map(c => [['all', ['>=', ['number', ['get', layer.colors.rangeOptions.field]], c.min], ['<', ['number', ['get', layer.colors.rangeOptions.field]], c.max]], c.color]), layer.colors.default), layer.colors.default]
            }
          })()
          if (layer.shapes.type === 'circle') {
            mapLayer.type = 'circle'
            mapLayer.paint = {
              'circle-stroke-width': ['interpolate', ['linear'], ['zoom'],
                2, ['case', ['boolean', ['feature-state', 'selected'], false], 2, 0.5],
                20, ['case', ['boolean', ['feature-state', 'selected'], false], 4, 1]
              ],
              'circle-stroke-color': ['case',
                ['boolean', ['feature-state', 'selected'], false],
                '#F00',
                '#000'

              ],
              'circle-stroke-opacity': 0.5,
              'circle-opacity': 0.7
            }
            if (layer.shapes.radius.type === 'constant') {
              mapLayer.paint['circle-radius'] = { stops: [[2, layer.shapes.radius.value], [11, layer.shapes.radius.value * 2], [20, layer.shapes.radius.value * 4]] }
            } else {
              mapLayer.paint['circle-radius'] = {
                property: layer.shapes.radius.field,
                stops: [
                  [{ zoom: 2, value: this.datasets[layer.dataset.id].minValueCircle }, layer.shapes.radius.radiusMin / 2], [{ zoom: 2, value: this.datasets[layer.dataset.id].maxValueCircle }, layer.shapes.radius.radiusMax / 2],
                  [{ zoom: 20, value: this.datasets[layer.dataset.id].minValueCircle }, layer.shapes.radius.radiusMin * 2], [{ zoom: 20, value: this.datasets[layer.dataset.id].maxValueCircle }, layer.shapes.radius.radiusMax * 2]
                ]
              }
            }
            mapLayer.paint['circle-color'] = layerColor
            this.map.addLayer(mapLayer, labelLayer)
            layers[layer.dataset.id] = { visible: !layer.hideAtStart, ids: [layer.dataset.id] }
          } else if (layer.shapes.type === 'shape') {
            this.map.addLayer({
              ...mapLayer,
              id: layer.dataset.id + '-fill',
              type: 'fill',
              filter: ['match', ['geometry-type'], ['Polygon'], true, false],
              paint: {
                'fill-color': layerColor,
                'fill-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0.6]
              }
            }, labelLayer)
            mapLayer.id = layer.dataset.id
            mapLayer.type = 'line'
            mapLayer.paint = {
              'line-color': layerColor,
              'line-width': ['interpolate', ['linear'], ['zoom'],
                2, ['case', ['boolean', ['feature-state', 'selected'], false], 5, 2],
                22, ['case', ['boolean', ['feature-state', 'selected'], false], 15, 6]
              ],
              'line-blur': 1
            }
            mapLayer.filter = ['match', ['geometry-type'], ['LineString'], true, false]
            this.map.addLayer(mapLayer, labelLayer)
            layers[layer.dataset.id] = { visible: !layer.hideAtStart, ids: [layer.dataset.id, layer.dataset.id + '-fill'] }
          } else if (layer.shapes.type === 'line') {
            this.map.addLayer({
              ...mapLayer,
              id: layer.dataset.id,
              type: 'line',
              filter: ['match', ['geometry-type'], ['Polygon', 'LineString'], true, false],
              paint: {
                'line-color': layerColor,
                'line-width': ['interpolate', ['linear'], ['zoom'], 2, layer.shapes.lineWidth * 0.3, 22, layer.shapes.lineWidth * 0.6],
                'line-blur': 1
              }
            }, labelLayer)
            // mapLayer.id = layer.dataset.id
            // mapLayer.type = 'line'
            // mapLayer.paint = {
            //   'line-color': layerColor,
            //   'line-width': ['interpolate', ['linear'], ['zoom'],
            //     2, ['case', ['boolean', ['feature-state', 'selected'], false], 5, 2],
            //     22, ['case', ['boolean', ['feature-state', 'selected'], false], 15, 6]
            //   ],
            //   'line-blur': 1
            // }
            // mapLayer.filter = ['match', ['geometry-type'], ['LineString'], true, false]
            // this.map.addLayer(mapLayer, labelLayer)
            layers[layer.dataset.id] = { visible: !layer.hideAtStart, ids: [layer.dataset.id, layer.dataset.id] }
          } else {
            const scale = (layer.shapes.scale || 100) / 100
            mapLayer.type = 'symbol'
            mapLayer.layout = {
              'icon-image': this.loadImages(layer),
              'icon-anchor': 'center',
              'icon-size': { stops: [[2, 0.2 * scale], [18, 0.8 * scale]] },
              'icon-allow-overlap': true
            }
            mapLayer.paint = {
              'icon-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 0, 1]
            }
            this.map.addLayer(mapLayer, labelLayer)
            this.map.addLayer({
              ...mapLayer,
              id: layer.dataset.id + '-selected',
              layout: {
                'icon-image': this.loadImages(layer, false),
                'icon-anchor': 'center',
                'icon-size': { stops: [[2, 0.4 * scale], [18, 1.6 * scale]] },
                'icon-allow-overlap': true
              },
              paint: {
                'icon-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0]
              }
            }, labelLayer)
            layers[layer.dataset.id] = { visible: !layer.hideAtStart, ids: [layer.dataset.id] }
          }
          if (layer.details && layer.details.length) {
            for (const id of layers[layer.dataset.id].ids) {
              // Change the cursor to a pointer when the it enters a feature.
              this.map.on('mouseenter', id, (e) => {
                this.map.getCanvas().style.cursor = 'pointer'
              })

              // Change it back to a pointer when it leaves.
              this.map.on('mouseleave', id, () => {
                this.map.getCanvas().style.cursor = ''
              })
            }
          }
        })
        this.$store.commit('setAny', { layers })
      })

      // "idle" event seems to be the best indication that the map is fully loaded
      this.map.on('idle', () => {
        if (window.triggerCapture && this.map.getSource('data-fair')) window.triggerCapture()
        this.loading = false
      })
    },
    selectionDetails (pos) {
      this.tooltipPos = pos
      const lngLat = [this.tooltipPos[1], this.tooltipPos[0]]
      if (this.popup) this.popup.remove()
      const features = this.map.queryRenderedFeatures(this.map.project(lngLat), {
        layers: [].concat(...this.config.layers.filter(l => l.details && l.details.length).map(l => this.layers[l.dataset.id].ids))
      })
      if (this.selected) {
        this.selected.forEach(feature => {
          this.map.setFeatureState({
            source: feature.source,
            sourceLayer: 'results',
            id: feature.id
          }, {
            selected: false
          })
        })
        this.$store.commit('setAny', { selected: null })
      }
      if (features && features.length) {
        features.slice(0, 1).forEach(feature => {
          this.map.setFeatureState({
            source: feature.source,
            sourceLayer: 'results',
            id: feature.id
          }, {
            selected: true
          })
        })
        this.$store.commit('setAny', { selected: features.slice(0, 1) })
      }
    },
    bboxPasted () {
      const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
      navigator.clipboard.writeText(bbox)
      if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'bbox', value: bbox } }, '*')
    },
    params (p) {
      const params = new URLSearchParams(window.location.search)
      return params.get(p)
    },
    loadImages (layer, load = true) {
      if (load) {
        if (layer.shapes.type === 'icon-multiple') {
          [].concat(layer.shapes.icons, { value: '__default', icon: layer.shapes.default }).forEach(icon => {
            if (layer.colors.type === 'single') {
              const img = new Image(64, 64)
              img.onload = () => this.map.addImage(layer.dataset.id + '--' + icon.value, img)
              img.src = svgToDataURL(icon.icon.svg.replace('path d', `path fill="${layer.colors.value}" stroke="white" d`))
            } else {
              [].concat(colors(layer.colors, this.datasets[layer.dataset.id].minValueColor, this.datasets[layer.dataset.id].maxValueColor, this.datasets[layer.dataset.id].quantiles), { value: '__default', color: layer.colors.default }).forEach(c => {
                const img = new Image(64, 64)
                img.onload = () => this.map.addImage(layer.dataset.id + '--' + icon.value + '--' + c.value, img, { sdf: false })
                img.src = svgToDataURL(icon.icon.svg.replace('path d', `path fill="${c.color}" stroke="white" d`))
              })
            }
          })
        } else if (layer.shapes.type === 'icon-single') {
          if (layer.colors.type === 'single') {
            const img = new Image(64, 64)
            img.onload = () => this.map.addImage(layer.dataset.id + '--default', img)
            img.src = svgToDataURL(layer.shapes.icon.svg.replace('path d', `path fill="${layer.colors.value}" stroke="white" d`))
          } else {
            [].concat(colors(layer.colors, this.datasets[layer.dataset.id].minValueColor, this.datasets[layer.dataset.id].maxValueColor, this.datasets[layer.dataset.id].quantiles), { value: '__default', color: layer.colors.default }).forEach(c => {
              const img = new Image(64, 64)
              img.onload = () => this.map.addImage(layer.dataset.id + '--' + c.value, img)
              img.src = svgToDataURL(layer.shapes.icon.svg.replace('path d', `path fill="${c.color}" stroke="white" d`))
            })
          }
        }
      }

      var iconImage
      if (layer.shapes.type === 'icon-multiple') {
        if (layer.colors.type === 'single') {
          iconImage = ['concat', layer.dataset.id, '--', ['case', ['has', layer.shapes.field], ['to-string', ['get', layer.shapes.field]], '__default']]
        } else {
          var colorExp
          if (layer.colors.type === 'enum') {
            colorExp = ['case', ['has', layer.colors.enumOptions.field], ['to-string', ['get', layer.colors.enumOptions.field]], '__default']
          } else if (layer.colors.type === 'range') {
            colorExp = ['case', ['has', layer.colors.rangeOptions.field], [].concat('case', ...colors(layer.colors, this.datasets[layer.dataset.id].minValueColor, this.datasets[layer.dataset.id].maxValueColor, this.datasets[layer.dataset.id].quantiles).map((c, i) => [['all', ['>=', ['number', ['get', layer.colors.rangeOptions.field]], c.min], ['<', ['number', ['get', layer.colors.rangeOptions.field]], c.max]], c.value]), '__default'), '__default']
          }
          iconImage = ['concat', layer.dataset.id, '--',
            ['case', ['has', layer.shapes.field], ['to-string', ['get', layer.shapes.field]], '__default'],
            '--',
            colorExp
          ]
        }
      } else if (layer.shapes.type === 'icon-single') {
        if (layer.colors.type === 'single') {
          iconImage = layer.dataset.id + '--default'
        } else if (layer.colors.type === 'enum') {
          iconImage = ['concat', layer.dataset.id, '--', ['case', ['has', layer.colors.enumOptions.field], ['to-string', ['get', layer.colors.enumOptions.field]], '__default']]
        } else if (layer.colors.type === 'range') {
          iconImage = ['concat', layer.dataset.id, '--', ['case', ['has', layer.colors.rangeOptions.field], [].concat('case', ...colors(layer.colors, this.datasets[layer.dataset.id].minValueColor, this.datasets[layer.dataset.id].maxValueColor, this.datasets[layer.dataset.id].quantiles).map((c, i) => [['all', ['>=', ['number', ['get', layer.colors.rangeOptions.field]], c.min], ['<', ['number', ['get', layer.colors.rangeOptions.field]], c.max]], c.value]), '__default'), '__default']]
        }
      }
      return iconImage
    }
  }
}
</script>

<style lang="css">
</style>
